import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from 'react-hot-toast';
import Loader from "../common/Loader";
import AdminLayout from "./AdminLayout";
import { GetManagementCostDetail, UpdateManagementCostMC,ListManagementCostHead } from "../store/slices/management-cost.js";

const EditManagementCost = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: paramId } = useParams();
  const location = useLocation();
  const passedPhase = location?.state?.phase || null;
  // also accept query param ?date=YYYY-MM-DD
  const searchParams = new URLSearchParams(location.search);
  const queryDate = searchParams.get('date');
  const idToLoad = paramId || 1; // default to 1 if not provided

    const user = JSON.parse(localStorage.getItem('user'));
  const UserID = user?.data?.profile_id;

  const [isLoading, setIsLoading] = useState(false);
  const [costRows, setCostRows] = useState([]);
  const [formDataState, setFormDataState] = useState({ management_cost_date: new Date().toISOString().split('T')[0], id: idToLoad });
  const [filesByHead, setFilesByHead] = useState({});

  const parseAmount = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const totalApplied = costRows.reduce((sum, row) => sum + parseAmount(row.applied_amount), 0);
  const totalReceived = costRows.reduce((sum, row) => sum + parseAmount(row.received_amount), 0);

  const resolveFileUrl = (fileName) => {
    if (!fileName) return '';
    let normalized = fileName;

    if (typeof normalized === 'string' && normalized.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(normalized);
        if (Array.isArray(parsed) && parsed.length > 0) {
          normalized = parsed[0];
        }
      } catch (error) {
        // keep original value if parsing fails
      }
    }

    if (/^https?:\/\//i.test(normalized)) return normalized;
    return `https://api.kisaankhata.com/management-cost/download/${encodeURIComponent(normalized)}`;
  };



  useEffect(() => {
    setIsLoading(true);
    const fetchPhase = passedPhase || queryDate || formDataState.management_cost_date;

    // load all cost heads first
    dispatch(ListManagementCostHead({ profile_id: UserID }))
      .then((headAction) => {
        const headRes = headAction.payload || headAction;
        const headList = headRes?.data || headRes?.result || headRes || [];
        const heads = Array.isArray(headList) ? headList : headList.data || [];

        // then load phase items and merge
        dispatch(GetManagementCostDetail({ profile_id: UserID, phase: fetchPhase }))
          .then((action) => {
            const res = action.payload || action;
            let items = [];
            if (Array.isArray(res)) {
              items = res;
            } else if (Array.isArray(res?.data)) {
              items = res.data;
            } else if (Array.isArray(res?.result)) {
              items = res.result;
            } else if (res?.management_cost && Array.isArray(res.management_cost.items)) {
              items = res.management_cost.items;
            }

            const first = items && items.length > 0 ? items[0] : {};

            setFormDataState((prev) => ({
              ...prev,
              phase: passedPhase || queryDate || first.phase || prev.phase,
              id: idToLoad,
            }));

            const itemMap = (items || []).reduce((acc, it) => {
              const key = String(it.cost_head_id || it.id || '');
              acc[key] = it;
              return acc;
            }, {});

            const rows = (heads || []).map((h) => {
              const key = String(h.id || h.cost_head_id || '');
              const matched = itemMap[key] || {};
              return {
                cost_head_id: h.id,
                name: h.head_name || h.name || '',
                applied_amount: matched.applied_amount || matched.applied_amount || matched.amount || '',
                received_amount: matched.received_amount || '',
                file: null,
                file_name: matched.file_name || h.file_name || '',
              };
            });

            setCostRows(rows);
          })
          .catch(() => {
            toast.error('Failed to load management cost detail');
          })
          .finally(() => setIsLoading(false));
      })
      .catch(() => {
        toast.error('Failed to load management cost heads');
        setIsLoading(false);
      });
  }, [dispatch, idToLoad, passedPhase, queryDate, formDataState.management_cost_date]);

  const handleTableChange = (index, field, value) => {
    setCostRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleFileChange = (headId, selectedFiles) => {
    setFilesByHead((prev) => ({
      ...prev,
      [headId]: selectedFiles,
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    setIsLoading(true);
    // Debug current values used to build FormData
    console.debug('formDataState before submit:', formDataState);
    console.debug('costRows before submit:', costRows);

    const fd = new FormData();
    fd.append('profile_id', UserID);
    // send 'phase' instead of management_cost_date to match Add page
    fd.append('phase', formDataState.phase);

    costRows.forEach((r) => {
      fd.append('cost_head_id[]', r.cost_head_id);
      fd.append('applied_amount[]', r.applied_amount || r.amount || '0');
      const selectedFiles = filesByHead[r.cost_head_id];
      if (selectedFiles && selectedFiles.length > 0) {
        Array.from(selectedFiles).forEach((file) => {
          fd.append(`file_name[${r.cost_head_id}][]`, file);
        });
      }
      fd.append('received_amount[]', r.received_amount || '0');
    });

    // DEBUG: print FormData contents so DevTools shows actual payload
    try {
      // Logging FormData directly can appear empty in some browsers; iterate entries instead
      console.debug('Submitting FormData entries:');
      for (const entry of fd.entries()) {
        // entry[1] can be File or primitive
        if (entry[1] instanceof File) {
          console.debug(entry[0], entry[1].name, entry[1], entry[1].size, entry[1].type);
        } else {
          console.debug(entry[0], entry[1]);
        }
      }
    } catch (err) {
      console.debug('Failed to enumerate FormData', err);
    }

    console.log('Submitting form data:', fd);

    dispatch(UpdateManagementCostMC(fd))
      .then((action) => {
        const res = action.payload || action;
        console.log('UpdateManagementCostMC response', res);
        const ok = res && (
          res.status === true ||
          res.status === 'success' ||
          res.success === true ||
          res.code === 200 ||
          res.statusCode === 200 ||
          res.code === 201 ||
          (res.data && (res.data.status === true || res.data.success === true))
        );
        if (ok) {
          toast.success(res?.message || 'Management cost updated');
          navigate('/management-cost/list');
        } else {
          toast.error(res?.message || 'Failed to update management cost');
        }
      })
      .catch((err) => {
        console.error('UpdateManagementCostMC error', err);
        toast.error('Failed to update management cost');
      })
      .finally(() => setIsLoading(false));
  };

  const handleDiscard = () => navigate(-1);

  return (
    <AdminLayout>
      {isLoading && <Loader />}
      <div className="row content-body ">
        <div className="container">
          <div className="page-header">
            <div>
              <h2 className="main-content-title tx-24 mg-b-5">Edit Management Cost</h2>
            </div>
          </div>

          <div className="row mb-5">
            <div className="col-md-10">
              <div className="card custom-card">
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label
                        style={{ fontWeight: 700, fontSize: "20px" }}
                      >
                        {formDataState.phase || 'Management Cost'}
                      </label>
                    </div>
                  </div>

                  <div className="mb-3">
                    <table className="table table-bordered">
                      <thead>
                          <tr>
                            <th style={{ width: "5%" }}>SR</th>
                            <th>Name</th>
                            <th>Applied Amount</th>
                            <th>Received Amount</th>
                            <th>File</th>
                            <th>File Download</th>
                          </tr>
                        </thead>
                      <tbody>
                        {costRows.map((it, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>
                              <div className="form-control-plaintext">{it.name || ''}</div>
                            </td>
                            <td>
                              <div className="input-group">
                                <span className="input-group-text">₹</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={it.applied_amount}
                                  onChange={(e) => handleTableChange(idx, 'applied_amount', e.target.value)}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="input-group">
                                <span className="input-group-text">₹</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={it.received_amount || ''}
                                  onChange={(e) => handleTableChange(idx, 'received_amount', e.target.value)}
                                />
                              </div>
                            </td>
                            <td>
                              <input
                                type="file"
                                multiple
                                name={`file_name[${it.cost_head_id}][]`}
                                className="form-control"
                                onChange={(e) => handleFileChange(it.cost_head_id, e.target.files)}
                              />
                              {!!filesByHead[it.cost_head_id]?.length && (
                                <div className="mt-1">
                                  {Array.from(filesByHead[it.cost_head_id]).map((file) => file.name).join(', ')}
                                </div>
                              )}
                              {!filesByHead[it.cost_head_id]?.length && it.file_name && (
                                <div className="mt-1">{it.file_name}</div>
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-link p-0"
                                disabled={!resolveFileUrl(it.file_name)}
                                onClick={() => {
                                  const url = resolveFileUrl(it.file_name);
                                  if (url) window.open(url, '_blank', 'noreferrer');
                                }}
                              >
                                Download
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={2} style={{ fontWeight: 600 }}>Total</td>
                          <td style={{ fontWeight: 600 }}>₹ {totalApplied.toFixed(2)}</td>
                          <td style={{ fontWeight: 600 }}>₹ {totalReceived.toFixed(2)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="form-group">
                    <div className="row" style={{ textAlign: "right", paddingTop: "20px" }}>
                      <div className="col-md-12">
                        <button type="button" className="btn btn-default" onClick={handleSubmit}>
                          Update
                        </button>
                        &nbsp;
                        <button type="button" className="btn btn-cancel" onClick={handleDiscard}>
                          Cancel
                        </button>
                        &nbsp;
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </AdminLayout>
  );
};

export default EditManagementCost;
